import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import Modal from "react-modal";
import analytics from "web/script/analytics/analytics";
import facebookEvents from "web/script/analytics/facebook-events";
import { gettext } from "web/script/modules/django-i18n";
import environment from "web/script/modules/environment";
import requester from "web/script/modules/requester";
import url from "web/script/utils/url";
import Button from "../button/button";
import EmailCaptureForm from "../email-capture-form/email-capture-form";
import SVGIcon from "../svg-icon/svg-icon";
import styles from "./fb-product-landing-overlay.css";

function FBProductLandingOverlay({
    cookiePolicyLink,
    privacyPolicyLink,
    termsAndConditionsLink,
    imageUrl,
    productId,
    shortEUPolicy,
    socialEnabled,
    disableClose,
}) {
    const appElement = useRef(null);
    const [emailAddress, setEmailAddress] = useState("");
    const [formErrors, setFormErrors] = useState(undefined);
    const [marketingEmails, setMarketingEmails] = useState(false);
    const [overlayOpen, setOverlayOpen] = useState(true);
    const [privacyPolicy, setPrivacyPolicy] = useState(false);
    const [showDone, setShowDone] = useState(false);

    useEffect(() => {
        Modal.setAppElement(appElement.current);
        analytics.event("signup_recent_fb", "open_signup_form", "open_overlay", true);
    });

    function changeEmail(event) {
        setEmailAddress(event.target.value);
    }

    function triggerClose() {
        setOverlayOpen(false);
    }

    function onClose() {
        if (showDone) {
            analytics.event("signup_recent_fb", "close_signup_form_screen_2", "exit_signup");
        } else {
            if (disableClose) {
                return;
            }
            analytics.event("signup_recent_fb", "close_signup_form_screen_1", "exit_signup");
        }
        triggerClose();
    }

    function onContinueShoppingClick() {
        analytics.event("signup_recent_fb", "click_continue_button", "continue_button");
        triggerClose();
    }

    function handleLegalChange(privacyPolicyChecked, marketingEmailsChecked) {
        if (privacyPolicy !== privacyPolicyChecked || marketingEmails !== marketingEmailsChecked) {
            setPrivacyPolicy(privacyPolicyChecked);
            setMarketingEmails(marketingEmailsChecked);
        }
    }

    async function onSubmit(event) {
        event.preventDefault();
        const country = environment.get("country");
        analytics.event("signup_recent_fb", "click_signup_button", "signup_button");
        const body = {
            product_id: productId,
            country,
            reason: "save_for_later_fb_carousel",
            email: emailAddress,
            marketing_emails: marketingEmails,
            privacy_policy: privacyPolicy,
        };

        const parsedURL = url.parse(window.location);
        const nextParam = parsedURL.path;

        let jsonData = undefined;
        const queryParams = url
            .toQueryString({
                type: "back_in_stock",
                product_id: productId,
                email_capture: "true",
                next: nextParam,
            })
            .toString();
        try {
            jsonData = await requester.post(`/account/register/?${queryParams}`, body);
        } catch (e) {
            console.error(e);
            setFormErrors({ other: [gettext("account.login.with_magic_link.error_title")] });
            return;
        }
        if (jsonData && jsonData.success) {
            setShowDone(true);
            analytics.event("signup_recent_fb", "show_success_screen", "open_overlay");
            analytics.tagEvent(
                analytics.TAG_NETWORKS.FACEBOOK,
                facebookEvents.REGISTRATION_COMPLETE
            );
            window.location = jsonData.next;
        } else {
            const { errors } = jsonData.data;
            setFormErrors(errors);
        }
    }

    function onSocialClick(authProvider) {
        event.preventDefault();
        // signup_facebook, signup_google
        analytics.event("signup_recent_fb", "click_social_sign_up", `signup_${authProvider}`);

        if (privacyPolicy !== true || marketingEmails !== true) {
            setFormErrors({
                privacy_policy: [gettext("account.register.user_must_tick_both_boxes")],
            });
            return;
        }

        analytics.getGAClientID().then((gaClientId) => {
            // start with the current URL (including all query parameters)
            const parsedURL = url.parse(window.location);
            const path = parsedURL.path;
            const params = url.toQueryString(parsedURL.searchParams);
            const nextParam = `${path}?${params}`;
            const searchTerm = parsedURL.searchParams.get("q");
            const searchGender =
                parsedURL.searchParams.get("gender") || parsedURL.searchParams.get("meta_gender");
            const productId = parsedURL.searchParams.get("product_id");
            const reason = parsedURL.searchParams.get("reason");
            const designerId = parsedURL.searchParams.get("designer_id");
            const type = parsedURL.searchParams.get("type");
            // update the path to point to the correct auth provider

            parsedURL.path = `/social/login/${authProvider}/`;
            parsedURL.searchParams.set("next", nextParam);

            parsedURL.searchParams.set("isLogin", false);
            parsedURL.searchParams.set("marketingEmails", marketingEmails);
            parsedURL.searchParams.set("reason", reason);
            parsedURL.searchParams.set("clientId", gaClientId);

            parsedURL.searchParams.set("productId", productId);
            parsedURL.searchParams.set("designerId", designerId);
            parsedURL.searchParams.set("authType", type);
            parsedURL.searchParams.set("searchQuery", searchTerm);
            parsedURL.searchParams.set("searchGender", searchGender);

            // do the redirect
            window.location = url.unparse(parsedURL);
        });
    }

    let title = gettext("product.buy_area.recently_viewed_overlay.title");
    let submitText = gettext("account.register.return_lead.recently_viewed_items.step2_btn");
    if (disableClose) {
        title = gettext("saved_items.sign_up_save_for_later_fb_carousel_title");
        submitText = gettext("saved_items.sign_up_save_for_later_fb_carousel_cta");
    }

    const form = (
        <div>
            <div className={styles.header}>
                <h3 className={styles.title}>{title}</h3>
            </div>
            <div className={styles.images}>
                <img key={imageUrl} className={styles.image} src={imageUrl} />
            </div>
            <EmailCaptureForm
                captureType={"signup_recent_fb"}
                cookiePolicyLink={cookiePolicyLink}
                emailAddress={emailAddress}
                emailLabelText={gettext(
                    "account.register.return_lead.recently_viewed_items.email_label"
                )}
                formErrors={formErrors}
                onEmailChange={changeEmail}
                onLegalChange={handleLegalChange}
                onSubmit={onSubmit}
                privacyPolicyLink={privacyPolicyLink}
                submitText={submitText}
                termsAndConditionsLink={termsAndConditionsLink}
                shortEUPolicy={shortEUPolicy}
                showSocial={socialEnabled}
                onSocialClick={onSocialClick}
            />
        </div>
    );

    const doneDiv = (
        <div id="email-capture-done">
            <div className={styles.header}>
                <SVGIcon name="tag" className={styles.tagIcon} />
                <h3 className={styles.doneTitle}>
                    {gettext("account.register.return_lead.recently_viewed_items.step3_title")}
                </h3>
                <div className={styles.doneSubtitle}>
                    {gettext("account.register.return_lead.recently_viewed_items.step3_subtitle")}
                </div>
            </div>
            <Button styleType="tertiary" onClick={onContinueShoppingClick} fullWidth>
                {gettext("account.register.return_lead.recently_viewed_items.step3_btn")}
            </Button>
        </div>
    );

    const closeButton = (
        <button className={styles.closeButton} onClick={onClose}>
            <SVGIcon name="cross" className={styles.closeButtonIcon} flip />
        </button>
    );

    return (
        <div id="email-capture-overlay" ref={appElement}>
            <Modal
                appElement={document.body}
                isOpen={overlayOpen}
                onRequestClose={onClose}
                className={styles.modal}
                overlayClassName={styles.overlay}
            >
                {!disableClose && closeButton}
                <div className={styles.captureForm}>
                    {!showDone && form}
                    {showDone && doneDiv}
                </div>
            </Modal>
        </div>
    );
}

export default FBProductLandingOverlay;

FBProductLandingOverlay.propTypes = {
    cookiePolicyLink: PropTypes.string.isRequired,
    imageUrl: PropTypes.string,
    privacyPolicyLink: PropTypes.string.isRequired,
    productId: PropTypes.string,
    termsAndConditionsLink: PropTypes.string.isRequired,
    shortEUPolicy: PropTypes.bool,
    socialEnabled: PropTypes.bool,
    disableClose: PropTypes.bool,
};
